import classnames from 'classnames';
import React, { FC, useMemo } from 'react';
import {
    useAnalytics,
    useFeatureFlags,
    useAuthManager,
    Offer,
    PaymentPlace,
    TarifficatorPurchaseSuccessWidgetEvent,
} from '@funtech-int/yango-shared';
import { useInView } from 'react-intersection-observer';

import { Button } from '@/shared/components/Button/Button';
import { PayVia } from '@/shared/components/PayVia/PayVia';
import { useOfferWebAnalytics } from '@/shared/hooks/useOfferWebAnalytics';
import { useSubscriptionRefetch } from '@/shared/hooks/useSubscriptionRefetch';
import { usePaymentWidget } from '@/shared/hooks/usePaymentWidget';
import { InterpolatedOfferText } from '@/shared/components/InterpolatedOfferText/InterpolatedOfferText';
import { getPaymentIcons } from '@/shared/lib/payments';

import { featureFlagsSchema } from '../../lib/featureFlags';
import { createSubscriptionOfferAnalytics } from '../../lib/subscriptionOfferAnalytics';
import { createOnboardingAnalytics } from '../../lib/onboardingAnalytics';
import { useI18n } from '../../hooks/useOnboardingI18n';
import { MultiOffersPlans } from '../MultiOffersPlans/MultiOffersPlans';
import { MultiOfferShowcase } from '../MultiOfferShowcase/MultiOfferShowcase';
import { useMainSectionFallbackBackground } from '../../hooks/useMainSectionFallbackBackground';
import { StaticCover } from '../StaticCover/StaticCover';
import { useMultiOfferSuccessPurchase } from '../../hooks/useMultiOfferSuccessPurchase';
import { getSubscriptionOfferButtonText } from '../../lib/getSubscriptionOfferButtonText';
import { usePaywallContext } from '../../hooks/usePaywallContext';

import styles from './MultiOfferMainSection.module.css';

interface MultiOfferMainSectionProps {
    isFallback?: boolean;
}

export const MultiOfferMainSection: FC<MultiOfferMainSectionProps> = ({ isFallback = false }) => {
    const { offer, paymentPlace } = usePaywallContext();
    const onSuccessPurchase = useMultiOfferSuccessPurchase({
        billingFeatureNames: offer?.requestedFeatures,
    });

    const analytics = useAnalytics(createOnboardingAnalytics);

    const { ref: setElementRef } = useInView({
        triggerOnce: true,
        threshold: 0.8,
        onChange(inView: boolean) {
            if (inView) {
                analytics.showed();
            }
        },
    });

    if (!offer) {
        return null;
    }

    return (
        <MultiOfferMainSectionContent
            isFallback={isFallback}
            offer={offer}
            setElementRef={setElementRef}
            onSuccessPurchase={onSuccessPurchase}
            paymentPlace={paymentPlace}
        />
    );
};

interface MultiOfferMainSectionContentProps {
    isFallback?: boolean;
    offer: Offer;
    setElementRef: (element: HTMLDivElement | null) => void;
    onSuccessPurchase: () => void;
    paymentPlace?: PaymentPlace;
}

const MultiOfferMainSectionContent: FC<MultiOfferMainSectionContentProps> = ({
    setElementRef,
    isFallback = false,
    offer,
    onSuccessPurchase,
    paymentPlace,
}) => {
    const i18n = useI18n();
    const { isAuthorized } = useAuthManager();

    const { flags } = useFeatureFlags(featureFlagsSchema);
    const isWideMultiOfferDesktopEnabled = flags.get('isWideMultiOfferDesktopEnabled');
    const offers = paymentPlace?.offers;
    const isYearAnnualOfferEnabled = flags.get('isYearAnnualOfferEnabled');
    const customOverlay = isYearAnnualOfferEnabled ? styles.customOverlay : undefined;
    const isBadgeEnabled = Boolean(offers?.some((o) => o.offerTexts?.customTexts?.badgeText));

    const staticBackgroundUrl = useMainSectionFallbackBackground();

    const backgroundUrl =
        offer.offerTexts.customTexts.desktopBackgroundUrl ?? staticBackgroundUrl ?? '';

    const buttonText = getSubscriptionOfferButtonText(
        i18n,
        offer.offerTexts.buttonText ?? '',
        !isAuthorized
    );

    const offerAnalytics = useAnalytics(createSubscriptionOfferAnalytics);
    offerAnalytics.setData({
        buttonText,
        offer,
    });

    const { refetch: subscriptionRefetch } = useSubscriptionRefetch();
    const offerWebAnalytics = useOfferWebAnalytics(offer);

    const paymentWidget = usePaymentWidget();

    const controller = useMemo(() => {
        function onPurchaseSuccess(data: TarifficatorPurchaseSuccessWidgetEvent) {
            subscriptionRefetch();
            offerAnalytics.succeeded();
            offerWebAnalytics.purchaseSucceeded(data);
        }
        return {
            onOfferShow: () => {
                offerAnalytics.showed();
                offerWebAnalytics.offerShowed();
                paymentPlace?.analytics.offerShowed(offer);
            },
            onOfferClick: () => {
                offerAnalytics.navigated();
                offerWebAnalytics.offerClicked();
                paymentPlace?.analytics.offerClicked(offer);

                paymentWidget.open(paymentPlace, offer, {
                    onPurchaseSuccess,
                    onSuccessClose: onSuccessPurchase,
                });
            },
        };
    }, [
        offer,
        offerWebAnalytics,
        onSuccessPurchase,
        paymentWidget,
        paymentPlace,
        offerAnalytics,
        subscriptionRefetch,
    ]);

    const buttonTextWithDetails = offer.offerTexts?.buttonTextWithDetails;
    const buttonVariant = isAuthorized ? 'plus' : 'accent';
    const shouldShowAdditionText = Boolean(buttonTextWithDetails && isAuthorized);

    return (
        <div className={styles.root} ref={setElementRef}>
            <div className={styles.offerBlock}>
                <StaticCover
                    backgroundUrl={backgroundUrl}
                    withAdditionalGradient={!customOverlay}
                    customOverlayStyles={customOverlay}
                />
                <div className={styles.offerWrapper}>
                    {isFallback ? (
                        <h1 className={classnames(styles.title_base, styles.title)}>
                            {i18n('fallback_title', { br: <br /> })}
                        </h1>
                    ) : (
                        <MultiOffersPlans
                            titleClassName={styles.title}
                            offers={offers}
                            isWideMultiOfferDesktopEnabled={isWideMultiOfferDesktopEnabled}
                            offer={offer}
                        />
                    )}

                    {offer?.target && (
                        <div
                            className={classnames(
                                styles.purchaseButtonWrapper,
                                !isBadgeEnabled && styles.withoutBadge
                            )}
                        >
                            <Button
                                className={classnames(
                                    isWideMultiOfferDesktopEnabled && styles.wideMultiOffer
                                )}
                                size={52}
                                variant={buttonVariant}
                                onClick={controller.onOfferClick}
                                data-test-id="SubscriptionPurchaseButton"
                                onShow={controller.onOfferShow}
                            >
                                {buttonText}
                            </Button>

                            {shouldShowAdditionText && (
                                <InterpolatedOfferText
                                    text={buttonTextWithDetails}
                                    legals={offer?.catalogCompositeOffer?.legalInfo?.items}
                                    className={styles.purchaseButtonText}
                                />
                            )}
                            <PayVia
                                paymentIcons={getPaymentIcons(offer.offerTexts?.customTexts)}
                                onClick={controller.onOfferClick}
                            />
                        </div>
                    )}
                </div>
            </div>
            <MultiOfferShowcase className={styles.showcase} />
        </div>
    );
};
